import axiosClient from './axiosClient';

/**
 * Typed axios wrappers for `/api/admin/globals/tasks`.
 *
 * Backend contract lives in `server/src/routes/admin/globals/tasks.ts`.
 * Keep this file in lockstep with the route handlers — in particular the
 * PATCH body's `base_version` + 409 `{error:'version_conflict', current_version}`
 * shape, which the edit page relies on to distinguish a stale-save from
 * any other failure.
 */

/** History-row shape returned inside the GET /:code envelope. */
export interface TaskHistoryRow {
  version: number;
  task_name: string | null;
  task_type_code: string | null;
  task_category_code: string | null;
  self_report: boolean;
  confirm_report: boolean;
  is_subtask: boolean;
  platform_task_id: string | number | null;
  active: boolean;
  change_summary: string | null;
  change_type: 'create' | 'update' | 'delete' | 'publish' | 'rollback';
  created_at: string;
  created_by: string | null;
}

export interface AdminTaskRow {
  task_code: string;
  task_name: string | null;
  task_type_code: string | null;
  task_category_code: string | null;
  self_report: boolean;
  confirm_report: boolean;
  is_subtask: boolean;
  platform_task_id: string | number | null;
  active: boolean;
  current_version: number;
  updated_at: string;
  updated_by: string | null;
  /** Derived on the server; `unpublished` becomes wire-able in Round 3. */
  status: 'in_sync' | 'never_published' | 'unpublished';
}

export interface AdminTaskDetail extends AdminTaskRow {
  history: TaskHistoryRow[];
}

export interface ListTasksResponse {
  rows: AdminTaskRow[];
}

export interface ListTasksParams {
  sort?: 'task_code' | 'task_name' | 'updated_at' | 'current_version';
  dir?: 'asc' | 'desc';
  /** Backend currently supports `in_sync | never_published`. */
  status?: 'in_sync' | 'never_published';
}

export interface CreateTaskPayload {
  /** Optional — server auto-generates `tsk-<uuid>` when omitted. */
  task_code?: string;
  task_name: string;
  task_type_code: string;
  task_category_code: string;
  self_report?: boolean;
  confirm_report?: boolean;
  is_subtask?: boolean;
  change_summary?: string;
}

export interface UpdateTaskPayload {
  base_version: number;
  task_name?: string;
  task_type_code?: string;
  task_category_code?: string;
  self_report?: boolean;
  confirm_report?: boolean;
  is_subtask?: boolean;
  /**
   * Active flag — used by Reactivate / soft-Deactivate flows. Backend
   * accepts it on the same PATCH endpoint; Round-3 UI wires the separate
   * list-row Deactivate action through here as well.
   */
  active?: boolean;
  change_summary?: string;
}

/* ------------------------------------------------------------------ */
/*  Publish + Rollback (Round 3)                                       */
/* ------------------------------------------------------------------ */

/**
 * Publish request. Client sends the specific task codes it wants to push
 * plus each entity's expected `base_version` so the server can reject
 * concurrent edits. A `batch_id` is optional — backend mints one if
 * absent — and flows back through the result so the UI can link to
 * history in a future round.
 */
export interface PublishTaskEntityRequest {
  task_code: string;
  base_version: number;
}

export interface PublishTasksRequest {
  entities: PublishTaskEntityRequest[];
  /**
   * Optional client-minted batch id. The R4 publish dialog mints once per
   * dialog-open and reuses across retries so the server can dedupe /
   * group partial-failure retries under one batch history row.
   */
  batch_id?: string;
  /** Optional free-text note stored on each resulting publish history row. */
  change_summary?: string;
}

export interface PublishTaskEntityResult {
  task_code: string;
  ok: boolean;
  /** On success: the new published version. */
  published_version?: number;
  /** On failure: short machine code (e.g. `version_conflict`, `not_found`). */
  error?: string;
  /** On failure: human-readable copy the UI can render verbatim. */
  message?: string;
  /** When `error === 'version_conflict'`, the backend's latest known version. */
  current_version?: number;
}

export interface PublishTasksResponse {
  batch_id: string;
  results: PublishTaskEntityResult[];
}

export interface RollbackTaskPayload {
  /** Version to restore to (contents of this version get copied forward). */
  target_version: number;
  /**
   * Optimistic-lock check — must equal the task's *current* version so
   * stale tabs get a 409 instead of clobbering a newer save.
   */
  base_version: number;
  change_summary?: string;
}

export const adminTaskApi = {
  list: async (params: ListTasksParams = {}): Promise<AdminTaskRow[]> => {
    const res = await axiosClient.get<ListTasksResponse>('/api/admin/globals/tasks', {
      params,
    });
    return res.data.rows;
  },

  get: async (code: string): Promise<AdminTaskDetail> => {
    const res = await axiosClient.get<AdminTaskDetail>(
      `/api/admin/globals/tasks/${encodeURIComponent(code)}`,
    );
    return res.data;
  },

  create: async (payload: CreateTaskPayload): Promise<AdminTaskRow> => {
    const res = await axiosClient.post<AdminTaskRow>('/api/admin/globals/tasks', payload);
    return res.data;
  },

  update: async (code: string, payload: UpdateTaskPayload): Promise<AdminTaskRow> => {
    const res = await axiosClient.patch<AdminTaskRow>(
      `/api/admin/globals/tasks/${encodeURIComponent(code)}`,
      payload,
    );
    return res.data;
  },

  /**
   * Bulk publish. Server processes each entity transactionally — partial
   * failures (e.g. one row hit a version_conflict) return `ok:false` on
   * that row but leave the successful rows committed. The UI shows a
   * per-row status + retry for the failed ones.
   */
  publish: async (payload: PublishTasksRequest): Promise<PublishTasksResponse> => {
    const res = await axiosClient.post<PublishTasksResponse>(
      '/api/admin/globals/tasks/publish',
      payload,
    );
    return res.data;
  },

  /**
   * Single-entity publish convenience used by the edit page's "Publish
   * this entity" button. Same wire format as bulk with one element so the
   * backend only has to handle one endpoint shape.
   */
  publishOne: async (
    code: string,
    base_version: number,
    options?: { batch_id?: string; change_summary?: string } | string,
  ): Promise<PublishTasksResponse> => {
    // Back-compat: older call sites pass a bare change_summary string.
    const opts =
      typeof options === 'string' ? { change_summary: options } : (options ?? {});
    return adminTaskApi.publish({
      entities: [{ task_code: code, base_version }],
      batch_id: opts.batch_id,
      change_summary: opts.change_summary,
    });
  },

  /**
   * Direct single-entity publish — hits POST /api/admin/globals/tasks/:code/publish
   * (the route that actually exists server-side). Used by the create-task
   * modal's "Save & Publish" action where we need a fast path that pushes the
   * just-created row into platform `task.task` without going through the
   * bulk-publish wrapper.
   */
  publishSingle: async (
    code: string,
    base_version: number,
    options?: { batch_id?: string; change_summary?: string },
  ): Promise<{
    new_version: number;
    platform_task_id: number | string | null;
    batch_id: string;
    status: 'in_sync';
  }> => {
    const res = await axiosClient.post(
      `/api/admin/globals/tasks/${encodeURIComponent(code)}/publish`,
      {
        base_version,
        batch_id: options?.batch_id,
        change_summary: options?.change_summary,
      },
    );
    return res.data;
  },

  /**
   * Restore a task to a prior version. Server copies the target version's
   * fields onto a newly-incremented `current_version` so history is
   * append-only. 409 on stale `base_version`.
   */
  rollback: async (code: string, payload: RollbackTaskPayload): Promise<AdminTaskRow> => {
    const res = await axiosClient.post<AdminTaskRow>(
      `/api/admin/globals/tasks/${encodeURIComponent(code)}/rollback`,
      payload,
    );
    return res.data;
  },
};
